import { createContext, useContext, useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useAuth } from "./useAuth";
import { TIER_KEYS } from "../data/peptidePricingTiers";
import {
  parseReferralParam,
  storeReferralData,
  getStoredReferralData,
  fetchReferralFromSession,
} from "../utils/patientReferralUtils";
import api from "../services/api";
import { saveCostData } from "../services/peptideApi";
import { useTranslation } from "react-i18next";
import { isDevPreview } from "../utils/devPreview";
import { DEFAULT_PEPTIDE_SHIPPING_METHODS, normalizeShippingMethods } from "../utils/peptideShippingMethods";
import { isProviderSurchargeEnabled } from "../components/peptides/peptideHelpers";
import { normalizeClinicalProfileRow, normalizePatientAccountRow } from "../utils/peptidePatient";

const peptidePricingContext = createContext();

function usePeptidePricingProvider() {
  const { t } = useTranslation();
  const { auth } = useAuth();
  const plan = auth?.session?.currentOrganizationPlan;
  const actualIsManufacturer = plan === "order-manufacturer";
  const actualIsPatient = plan === "order-patient";
  // A peptide provider is either an order-only peptides org (plan
  // "order-provider") OR any org with the peptides product line enabled — e.g.
  // a full "ultimate" plan running EHR/DME + peptides. Peptide ACCESS is gated
  // on `orgInfo.productLines.peptides` (GuardedPeptides), so the provider role
  // must follow the same signal. Keying the role solely off the exact plan
  // string left peptides-enabled non-order orgs with NO peptide role, so
  // refreshPricing never ran and the catalog/tier/pricing context stayed empty
  // (the catalog *page* still worked via its own fallback fetch, masking it).
  const hasPeptidesProductLine = auth?.orgInfo?.productLines?.peptides === true;
  const actualIsProvider = plan === "order-provider" || (hasPeptidesProductLine && !actualIsManufacturer && !actualIsPatient);

  // Sales reps (sales-rep / sales-rep-master groups) sit inside a manufacturer
  // org's plan but are *personal buyers* in the peptides module — distinct
  // from a real patient. They shop the catalog at the onboarded wholesale
  // tier their manufacturer assigned them, see clinical (not patient-facing)
  // copy because they're internal staff, but still check out through the
  // patient-style flow (PatientPeptideCheckout, ship-to-self, MyOrders).
  // Page-level personal-buyer logic at Peptides.js uses `isPatient ||
  // isSalesRep` to drive nav/checkout; product content components keep using
  // plain `isPatient` so reps see clinical descriptions, not patient copy.
  const groups = auth?.user?.groups || [];
  const isSalesRep = groups.indexOf("sales-rep") !== -1 || groups.indexOf("sales-rep-master") !== -1;
  // Onboarded rep tier. Source: `user.custom.peptideRepTier` (set during the
  // rep's onboarding by a manufacturer supervisor). Falls back to "A" — the
  // best wholesale tier — so a freshly-onboarded rep without an explicit
  // assignment still gets internal pricing, not retail. Real production must
  // round-trip this field via Org / User vertex updates (see backend guide).
  const repTier = isSalesRep ? auth?.user?.custom?.peptideRepTier || "A" : null;

  // Role is determined by org plan — no toggle in production.
  // Dev-preview can flip a provider tester into the patient view for QA;
  // the helper is dead in real production builds (gated in devPreview.js).
  // Mirrors the page-level override at Peptides.js:40 so the pricing layer
  // (activeTier, priceFor) flips to retail "W" instead of the tester's own
  // provider tier — without this, the dev-preview tester sees their org's
  // tiered pricing where a real patient would see retail.
  const peptidePatientPreview = isDevPreview("peptide-patient");
  const isManufacturer = actualIsManufacturer && !peptidePatientPreview && !isSalesRep;
  const isProvider = actualIsProvider && !peptidePatientPreview && !isSalesRep;
  const isPatient = actualIsPatient || peptidePatientPreview;

  // ── Patient referral data ──
  // Source priority: URL ?ref= > sessionStorage cache > backend session fetch.
  // URL/storage resolve synchronously so the first paint already has the data
  // for shared-link and within-session navigations. The backend fetch is
  // async and only fires for cpInvite-onboarded patients who have neither —
  // empty-state UI renders during the brief in-flight window.
  const [referralData, setReferralData] = useState(() => {
    const fromUrl = parseReferralParam(window.location.search);
    if (fromUrl) {
      storeReferralData(fromUrl);
      return fromUrl;
    }
    return getStoredReferralData();
  });

  useEffect(() => {
    if (referralData) return;
    if (!actualIsPatient) return;
    let cancelled = false;
    fetchReferralFromSession().then((result) => {
      if (cancelled || !result) return;
      storeReferralData(result);
      setReferralData(result);
    });
    return () => {
      cancelled = true;
    };
  }, [actualIsPatient, referralData]);

  // Tier pricing fetched from backend (variant → { A, B, C, D })
  const [tierPricingData, setTierPricingData] = useState({});
  const [costData, setCostDataState] = useState({});

  // Product catalog (peptides + supplies) fetched from backend. All three
  // peptide endpoints (provider/manufacturer/patient) return a `catalog` array
  // alongside tier pricing. Exposed here so consumers (e.g. the sales-rep
  // drawer's per-variant editor) don't need a second fetch.
  const [catalog, setCatalog] = useState([]);

  // Checkout shipping methods. Provider/patient/rep catalog responses carry a
  // BE-resolved `shippingMethods` array; the manufacturer path reads the raw
  // DoctorOffice `shippingSettings` instead — normalizeShippingMethods accepts
  // both and always yields a non-empty list (defaults as the floor), matching
  // the BE's resolution so the picker shows exactly what the server prices.
  const [shippingMethods, setShippingMethods] = useState(DEFAULT_PEPTIDE_SHIPPING_METHODS);

  // Provider tier assignments (manufacturer-managed, fetched from backend)
  const [providerTiers, setProviderTiers] = useState({});

  // Per-provider custom price overrides keyed by providerOrgId → variantId → cents.
  // Layered above tier price during resolution: customPrice > tierPrice > variant default.
  // Source: manufacturer→provider OrganizationProvider edge.
  const [providerCustomPricingState, setProviderCustomPricing] = useState({});
  const [ownedPricingScope, setOwnedPricingScope] = useState({
    scopeKey: "",
    generation: 0,
  });
  const [ownedPricingLoad, setOwnedPricingLoad] = useState({
    scopeKey: "",
    generation: 0,
    status: "idle",
  });
  const [ownedPricingScopeEpoch, setOwnedPricingScopeEpoch] = useState({
    scopeKey: "",
    generation: 0,
  });

  // Sales-rep ownership fetch — extracted so refreshPricing can run it in
  // parallel with the catalog fetch AND so the drawer can call it on mount
  // to pick up writes made elsewhere (another rep, supervisor edit, etc.).
  // Returns `{ ok: boolean }` so the caller can decide whether to surface
  // a retry affordance. Stricter type guard than `typeof === "object"`
  // (which accepts arrays) — a bad payload fails the authoritative baseline
  // closed rather than leaking into downstream consumers.
  //
  // `t` is held via ref so a fresh translation function reference (the
  // react-i18next provider re-creates t on prop change) doesn't bust this
  // callback's memo identity. That identity feeds refreshPricing's deps,
  // which in turn feeds the auto-fetch useEffect — a t change would
  // otherwise re-fire the whole pipeline on every render.
  const tRef = useRef(t);
  tRef.current = t;
  const currentOwnedPricingScopeRef = useRef({
    scopeKey: "",
    generation: 0,
  });
  const appliedOwnedPricingScopeRef = useRef({
    scopeKey: "",
    generation: 0,
  });
  const ownedPricingRequestIdRef = useRef(0);
  const providerPricingSaveLocksRef = useRef(new Set());
  const loadOwnedProviderCustomPricing = useCallback(async ({ refresh = false } = {}) => {
    const { scopeKey: requestScopeKey, generation: requestGeneration } = currentOwnedPricingScopeRef.current;
    if (!requestScopeKey) return { ok: false };
    const requestId = ++ownedPricingRequestIdRef.current;
    const requestIsCurrent = () =>
      requestId === ownedPricingRequestIdRef.current &&
      requestScopeKey === currentOwnedPricingScopeRef.current.scopeKey &&
      requestGeneration === currentOwnedPricingScopeRef.current.generation;
    setOwnedPricingLoad({
      scopeKey: requestScopeKey,
      generation: requestGeneration,
      status: "loading",
    });
    try {
      const ownedMap = await api.getOwnedProviderCustomPricing();
      if (!requestIsCurrent()) {
        return { ok: true };
      }
      if (!ownedMap || typeof ownedMap !== "object" || Array.isArray(ownedMap)) {
        throw new Error("invalid_owned_pricing_payload");
      }
      const appliedScope = appliedOwnedPricingScopeRef.current;
      const scopeChanged = appliedScope.scopeKey !== requestScopeKey || appliedScope.generation !== requestGeneration;
      // Default (drawer-open auto-load): merge with prev spread last so an
      // empty or stale server snapshot can't wipe optimistic updates written
      // by a concurrent saveProviderCustomPricing call on the same drawer.
      // refresh=true (explicit on-demand refetch): the endpoint returns the
      // complete owned-pricing map, so treat it as authoritative and replace
      // outright — this also drops overrides removed server-side. In-progress
      // editor edits are unaffected (the editor guards them via its own dirty
      // state, not this baseline).
      setProviderCustomPricing((prev) => (refresh || scopeChanged ? ownedMap : { ...ownedMap, ...prev }));
      const appliedRequestScope = {
        scopeKey: requestScopeKey,
        generation: requestGeneration,
      };
      appliedOwnedPricingScopeRef.current = appliedRequestScope;
      setOwnedPricingScope(appliedRequestScope);
      setOwnedPricingLoad({
        ...appliedRequestScope,
        status: "ready",
      });
      return { ok: true };
    } catch (e) {
      if (!requestIsCurrent()) {
        return { ok: true };
      }
      setOwnedPricingLoad({
        scopeKey: requestScopeKey,
        generation: requestGeneration,
        status: "error",
      });
      // Log only status + message — the raw error carries `error.config.data`
      // (the request payload), and although this endpoint takes no params,
      // matching the rest of the codebase's PII-conservative pattern keeps
      // grep-for-PII audits clean.
      console.error("Failed to fetch owned provider custom pricing:", e?.response?.status, e?.message);
      // Surface the failure — silent fallback was leaving reps with empty
      // baselines they'd overwrite by re-saving, clobbering real BE state.
      toast.error(
        tRef.current(
          "hooks.usePeptidePricing.failedToLoadOwnedCustomPricing",
          "Could not load saved custom prices. Refresh to retry."
        )
      );
      return { ok: false };
    }
  }, []);
  const [providerRecurringDefaults, setProviderRecurringDefaults] = useState({});

  // Provider-side custom price map keyed by variantId → cents. This is the
  // calling provider's *own* slice of providerCustomPricing, projected by the
  // backend in getProviderPeptideCatalog so the provider can never see another
  // provider's overrides. Manufacturers / sales reps leave this at {}.
  const [ownCustomPricing, setOwnCustomPricing] = useState({});

  // Rep-side buy price map keyed by variantId → cents. The calling rep's own
  // slice of orgInfo.salesRepCustomPricing, projected by the BE into the
  // provider-peptide-catalog response (rep-session projection — cross-org RPC
  // flattens rep identity, same reason getOwnedProviderCustomPricing exists).
  // Read ONLY by resolvePrice's isSalesRep branch. Beats tier price; absent =
  // use tier (fail-closed).
  const [repCustomPricing, setRepCustomPricing] = useState({});

  // Active-form surcharge state. `activeSurchargeEnabled` is the current buyer's
  // own flag (provider/patient self-order), default true. `providerSurchargeDisabled`
  // is the manufacturer's full exceptions map for the admin settings page.
  const [activeSurchargeEnabled, setActiveSurchargeEnabled] = useState(true);
  const [providerSurchargeDisabled, setProviderSurchargeDisabled] = useState({});

  // Provider-set, single-layer retail price map keyed by variantId → cents.
  // Role-relative source (BE projects the right one):
  //   • provider catalog  → the provider's OWN retail prices (editable here)
  //   • patient catalog   → the patient's REFERRING provider's retail prices
  // Read ONLY by resolvePrice's patient branch and the provider pricing/
  // analytics surfaces. Never feeds tier/wholesale or rep/manufacturer math.
  const [providerRetailPricing, setProviderRetailPricing] = useState({});

  // Real provider (doctor) list fetched from backend
  const [realProviders, setRealProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);

  // Real patient list fetched from backend
  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const patientSearchTimer = useRef(null);

  // The active tier context — used when manufacturer is placing an order for a specific provider
  // For order providers, this is their assigned tier (resolved from providerTiers)
  const [orderContextTier, setOrderContextTier] = useState(null);

  // ── Order assignment state (manufacturer only) ──
  // "provider" → ordering on behalf of a downstream clinic (draft pipeline)
  // "patient"  → ordering for a specific named patient
  // "instore"  → walk-in / in-store sale; no provider or patient to attach,
  //              the manufacturer takes payment on the spot. Forces the
  //              "Collect Payment Now" flow in PeptideCheckout.
  const [orderAssignType, setOrderAssignType] = useState("patient");

  useEffect(() => {
    if (isManufacturer && orderAssignType === "patient") {
      setOrderAssignType("provider");
    }
  }, [isManufacturer, orderAssignType]);
  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [selectedPatientId, setSelectedPatientId] = useState("");

  // Fetch linked provider organizations from backend (via Manufacturer Stats process)
  useEffect(() => {
    if (!actualIsManufacturer) return;
    let cancelled = false;
    const fetchProviders = async () => {
      setProvidersLoading(true);
      try {
        const res = await api.getOrdersStats(0);
        if (cancelled) return;
        const orgs = res?.data?.output?.stats || [];
        setRealProviders(
          orgs.map((org) => ({
            id: org.organizationId,
            name: org.organizationName || org.organizationId,
            contactEmail: org.orgInfo?.officeEmail || "",
            contactPhone: org.orgInfo?.officePhone || "",
            orgInfo: org.orgInfo || {},
          }))
        );
      } catch (e) {
        console.error("Failed to fetch providers", e);
      } finally {
        if (!cancelled) setProvidersLoading(false);
      }
    };
    fetchProviders();
    return () => {
      cancelled = true;
    };
  }, [actualIsManufacturer]);

  // Fetch tier pricing + provider tier assignments from backend using the
  // real session role. Dev patient preview still needs the provider/manufacturer
  // catalog source so QA sees the same catalog those roles can currently fetch;
  // patient rendering/pricing rules are applied below.
  // Tracks whether the first refresh has resolved so consumers (e.g. the cart's
  // sessionStorage migration) can avoid acting on the empty initial state and
  // POSTing stale local data over fresh remote data that hasn't loaded yet.
  const [pricingLoaded, setPricingLoaded] = useState(false);
  const isPeptidePlan = actualIsManufacturer || actualIsProvider || actualIsPatient;
  // `pricingLoaded` is intentionally sticky for legacy consumers: on refresh
  // failure they continue using the last known catalog instead of blocking
  // forever. Money-sensitive editors need a stricter signal, though. Track the
  // catalog request against the exact organization + endpoint role so retained
  // data can never be mistaken for a successful response in a new scope.
  const catalogRole = isSalesRep
    ? "sales-rep"
    : actualIsManufacturer
      ? "manufacturer"
      : actualIsPatient
        ? "patient"
        : actualIsProvider
          ? "provider"
          : "";
  const catalogOrganizationName = auth?.session?.currentOrganizationName;
  const catalogActorId = typeof auth?.user?.id === "string" ? auth.user.id.trim() : "";
  const currentCatalogScopeKey =
    catalogRole && catalogActorId && typeof catalogOrganizationName === "string" && catalogOrganizationName.trim()
      ? JSON.stringify([catalogOrganizationName.trim(), catalogRole, catalogActorId])
      : "";
  const currentCatalogScopeRef = useRef(currentCatalogScopeKey);
  currentCatalogScopeRef.current = currentCatalogScopeKey;
  // A key can recur after an in-session actor transition (A → B → A). Treat
  // each committed contiguous stay in a key as a distinct generation so an
  // optimistic map or late request from the first A session cannot become
  // current merely because the string key matches again. The epoch advances
  // in a layout effect: mutating it during render would let an abandoned
  // concurrent render corrupt the last committed actor's request boundary.
  const ownedPricingScopeTransitionPending = ownedPricingScopeEpoch.scopeKey !== currentCatalogScopeKey;
  const currentOwnedPricingScopeGeneration = ownedPricingScopeTransitionPending
    ? ownedPricingScopeEpoch.generation + 1
    : ownedPricingScopeEpoch.generation;
  useLayoutEffect(() => {
    const committedScope = {
      scopeKey: currentCatalogScopeKey,
      generation: currentOwnedPricingScopeGeneration,
    };
    currentOwnedPricingScopeRef.current = committedScope;
    if (ownedPricingScopeTransitionPending) {
      setOwnedPricingScopeEpoch(committedScope);
    }
  }, [currentCatalogScopeKey, currentOwnedPricingScopeGeneration, ownedPricingScopeTransitionPending]);
  const providerCustomPricing = useMemo(
    () =>
      isSalesRep &&
      (ownedPricingScope.scopeKey !== currentCatalogScopeKey ||
        ownedPricingScope.generation !== currentOwnedPricingScopeGeneration)
        ? {}
        : providerCustomPricingState,
    [currentCatalogScopeKey, currentOwnedPricingScopeGeneration, isSalesRep, ownedPricingScope, providerCustomPricingState]
  );
  const ownedPricingReadyForCurrentScope =
    !isSalesRep ||
    (Boolean(currentCatalogScopeKey) &&
      ownedPricingScope.scopeKey === currentCatalogScopeKey &&
      ownedPricingScope.generation === currentOwnedPricingScopeGeneration &&
      ownedPricingLoad.scopeKey === currentCatalogScopeKey &&
      ownedPricingLoad.generation === currentOwnedPricingScopeGeneration &&
      ownedPricingLoad.status === "ready");
  const ownedPricingLoadStatus = !isSalesRep
    ? "ready"
    : !currentCatalogScopeKey
      ? "idle"
      : ownedPricingLoad.scopeKey === currentCatalogScopeKey && ownedPricingLoad.generation === currentOwnedPricingScopeGeneration
        ? ownedPricingLoad.status
        : "loading";
  const catalogRequestIdRef = useRef(0);
  const [catalogFreshness, setCatalogFreshness] = useState({
    scopeKey: "",
    status: "idle",
  });
  const catalogReadyForCurrentScope =
    Boolean(currentCatalogScopeKey) &&
    catalogFreshness.scopeKey === currentCatalogScopeKey &&
    catalogFreshness.status === "ready";
  const catalogLoadStatus = !currentCatalogScopeKey
    ? "idle"
    : catalogFreshness.scopeKey === currentCatalogScopeKey
      ? catalogFreshness.status
      : "loading";

  const refreshPricing = useCallback(async () => {
    const requestScopeKey = currentCatalogScopeKey;
    const requestId = ++catalogRequestIdRef.current;
    const requestIsCurrent = () =>
      requestId === catalogRequestIdRef.current && requestScopeKey === currentCatalogScopeRef.current;

    setCatalogFreshness({
      scopeKey: requestScopeKey,
      status: requestScopeKey ? "loading" : "idle",
    });

    try {
      // Sales reps need a second fetch (`getOwnedProviderCustomPricing`) that
      // is independent of the catalog data. Kick both off together so the
      // drawer load isn't paying two serial round-trips. The ownership fetch
      // self-handles failure (toast + log), so we don't `await` it inside
      // this `try` until later — the catalog error path stays first-class.
      const ownedPricingPromise = isSalesRep ? loadOwnedProviderCustomPricing() : null;

      let data;
      if (isSalesRep) {
        // Sales reps sit on `order-manufacturer` but shouldn't see the
        // manufacturer admin payload (cost data, every provider's custom
        // pricing, the whole tier matrix). The provider endpoint returns the
        // same product catalog plus tier prices — exactly what we need to
        // resolve the rep's onboarded tier — without leaking admin fields.
        data = await api.getProviderPeptideCatalog();
      } else if (actualIsManufacturer) {
        data = await api.getPeptideData();
      } else if (actualIsPatient) {
        // S07 Phase 2A: pass the referring provider id so the server projects
        // that provider's retail (patientPricing). referralData resolves from
        // ?ref= / sessionStorage / an async session fetch — refreshPricing
        // re-runs when it lands (referralData is in this callback's deps).
        data = await api.getPatientPeptideCatalog(referralData?.referringProviderId);
      } else {
        data = await api.getProviderPeptideCatalog();
      }
      // An older organization/role response must not write any shared pricing
      // state. The ref is updated during render, so this guard closes the gap
      // before the new scope's passive effect starts its own request.
      if (!requestIsCurrent()) return;

      const catalogResponseIsValid = Array.isArray(data?.catalog);
      // Catalog is identical-shape across all three endpoints; safe to store
      // for every role. The per-variant editor (sales-rep drawer) reads it.
      if (catalogResponseIsValid) setCatalog(data.catalog);
      // Provider/patient/rep responses carry `shippingMethods`; the
      // manufacturer's getPeptideData carries raw `shippingSettings`.
      setShippingMethods(normalizeShippingMethods(data.shippingMethods ?? data.shippingSettings));
      // Real patients should not retain provider/manufacturer tier data.
      // Dev patient preview runs under a provider/manufacturer session, so it
      // keeps fetched tier pricing only to resolve activeTier "W" retail.
      setTierPricingData(actualIsPatient ? {} : data.tierPricing || {});
      // providerTiers powers the manufacturer's per-provider order context AND
      // the provider's own activeTier (catalog projects the single relevant
      // entry — see `ownTier` resolution below). Sales reps shouldn't see it
      // (no surface uses it for them) and patients shouldn't either. Always
      // overwrite for the eligible roles, defaulting to {} when the BE omits
      // the field, so a removed tier assignment can't be masked by a prior
      // refetch's stale map winning the new precedence at line 397.
      if (!isPatient && !isSalesRep) setProviderTiers(data.providerTiers || {});
      // Custom-pricing map per provider org. Manufacturers see all providers in
      // their org; sales reps see only orgs they directly own (BE filters).
      // The shape `{ providerOrgId: { variantId: cents } }` is identical in both
      // cases — only the payload size differs.
      if (isManufacturer && data.providerCustomPricing) {
        setProviderCustomPricing(data.providerCustomPricing);
      }
      if (actualIsManufacturer) {
        setProviderSurchargeDisabled(data.providerSurchargeDisabled || {});
      }
      // Sales reps don't get providerCustomPricing from the catalog endpoint
      // (cross-org RPC flattens identity to the manufacturer, so the BE can't
      // tell which rep is calling). The dedicated endpoint runs in the rep's
      // own session — kicked off above in parallel with the catalog fetch.
      // Await it here so `pricingLoaded` doesn't flip true before its result
      // (or its toast on failure) lands. Backend: lucasai-backend PR #101.
      if (ownedPricingPromise) {
        await ownedPricingPromise;
      }
      if (!requestIsCurrent()) return;
      if (isManufacturer && data.providerRecurringDefaults) {
        setProviderRecurringDefaults(data.providerRecurringDefaults);
      }
      // Providers (not manufacturers, not sales reps, not patients) get their
      // own variant→cents map projected by the backend. We deliberately gate
      // on isProvider so a sales-rep response that accidentally includes
      // customPricing cannot bleed into rep pricing — reps buy at repTier.
      if (isProvider && data.customPricing) {
        setOwnCustomPricing(data.customPricing);
      } else if (!isProvider) {
        setOwnCustomPricing({});
      }
      // Sales reps get their own buy-side custom pricing projected by the BE.
      // Gate on isSalesRep so a non-rep response can't bleed overrides in.
      if (isSalesRep && data.repCustomPricing) {
        setRepCustomPricing(data.repCustomPricing);
      } else if (!isSalesRep) {
        setRepCustomPricing({});
      }
      // Providers get their own retail map; real patients (and dev preview)
      // get their referring provider's. Everyone else stays at {}.
      if ((isProvider || isPatient) && data.patientPricing) {
        setProviderRetailPricing(data.patientPricing);
      } else if (!isProvider && !isPatient) {
        setProviderRetailPricing({});
      }
      // Provider/patient self-order: their own (or referring provider's) flag.
      // Default true when the field is absent (pre-B3 backend, or unset provider).
      if (isProvider || isPatient) {
        setActiveSurchargeEnabled(data.activeSurchargeEnabled !== false);
      }
      if (data.costData) setCostDataState(data.costData);
      setPricingLoaded(true);
      setCatalogFreshness({
        scopeKey: requestScopeKey,
        status: requestScopeKey && catalogResponseIsValid ? "ready" : "error",
      });
    } catch (e) {
      console.error("Failed to fetch peptide pricing:", e?.message || "unknown error");
      if (!requestIsCurrent()) return;
      // Even on failure, mark loaded so guarded consumers don't block forever —
      // they'll proceed with whatever local cache they have.
      setPricingLoaded(true);
      setCatalogFreshness({
        scopeKey: requestScopeKey,
        status: requestScopeKey ? "error" : "idle",
      });
    }
  }, [
    actualIsManufacturer,
    actualIsPatient,
    isManufacturer,
    isPatient,
    isProvider,
    isSalesRep,
    loadOwnedProviderCustomPricing,
    currentCatalogScopeKey,
    // Re-fetch the patient catalog once the referral resolves (async), so the
    // patient picks up their referring provider's retail (S07 Phase 2A).
    referralData,
  ]);

  useEffect(() => {
    if (!isPeptidePlan) return;
    let cancelled = false;
    refreshPricing().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [isPeptidePlan, refreshPricing]);

  // Load clinical profiles on mount as default patient list
  const loadDefaultPatients = useCallback(async () => {
    setPatientsLoading(true);
    try {
      const res = await api.listClinicalProfiles();
      const profiles = res?.data?.output?.profiles || [];
      setPatients(profiles.map(normalizeClinicalProfileRow).filter(Boolean));
    } catch (e) {
      console.error("Failed to load clinical profiles:", e?.message || "unknown error");
    } finally {
      setPatientsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isPeptidePlan) return;
    loadDefaultPatients();
  }, [isPeptidePlan, loadDefaultPatients]);

  // Search patients from backend (debounced)
  const searchPatients = useCallback((text) => {
    if (patientSearchTimer.current) clearTimeout(patientSearchTimer.current);
    if (!text || text.length < 2) {
      loadDefaultPatients();
      return;
    }
    patientSearchTimer.current = setTimeout(async () => {
      setPatientsLoading(true);
      try {
        const res = await api.listPatient(text);
        const users = (res?.data?.output?.users || []).filter((u) => {
          if (!u?.dob) return false;
          if (u.lastName?.toLowerCase() === "anonymous") return false;
          return true;
        });
        // dob arrives in multiple shapes across orgs (epoch millis, "M/D/YYYY",
        // ISO); normalizePatientAccountRow runs it through dobForDisplay for us.
        setPatients(users.map(normalizePatientAccountRow).filter(Boolean));
      } catch (e) {
        console.error("Failed to search patients", e);
      } finally {
        setPatientsLoading(false);
      }
    }, 400);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    return () => {
      if (patientSearchTimer.current) clearTimeout(patientSearchTimer.current);
    };
  }, []);

  // For order providers: resolve their own tier (patients don't have tiers).
  // `providerTiers` is the fresh value, projected on every catalog refetch
  // from `manufacturer.DoctorOffice.peptideProviderTiers[providerOrgId]` —
  // the only field the rep-side "Assign Provider Pricing Tier" process writes.
  // It MUST win over `auth.orgInfo.pricingTier`: that's a snapshot of the
  // provider's own DoctorOffice taken at login and never refreshes within a
  // session, so it goes stale the moment a rep edits the tier in the sales
  // dashboard. `ownPricingTier` is kept as a fallback for legacy orgs whose
  // entry might be missing from the manufacturer's map.
  //
  // Case-tolerant lookup: `currentOrganizationName` keeps its original case in
  // memory while BE-side org-slug keys are usually (but not universally)
  // lowercased — see peptideApi.js:154-166 for the documented hazard. Some
  // legacy orgs project mixed-case keys through, so we check BOTH the raw and
  // the lowercased orgName before falling through. Without this a mixed-case
  // mismatch on either side silently routes the catalog back to the stale
  // ownPricingTier and reproduces the very bug the precedence flip is meant
  // to eliminate — while the cart can still pick up the fresh value through
  // its own pricing path, making the bug look like a catalog-only divergence.
  const orgName = auth?.session?.currentOrganizationName;
  const ownPricingTier = auth?.orgInfo?.pricingTier;
  const ownTier = useMemo(() => {
    if (isManufacturer || isPatient || isSalesRep) return null;
    if (!orgName) return ownPricingTier || "W";
    const direct = providerTiers[orgName];
    if (direct) return direct;
    const lower = providerTiers[String(orgName).toLowerCase()];
    if (lower) return lower;
    return ownPricingTier || "W";
  }, [isManufacturer, isPatient, isSalesRep, providerTiers, orgName, ownPricingTier]);

  // The effective tier for price resolution.
  //
  // Tier "W" is "Website / Retail" per `data/peptidePricingTiers.js:11` —
  // the public retail price displayed in patient and manufacturer-browse
  // views. Tiers `min` and A through D are provider-side wholesale tiers
  // (`min` = near-cost floor, best discount; D = standard); W is retail.
  // The wholesale tiers MUST NOT reach a patient view (`rules/decisions.md`).
  //
  // Patients see W (retail). The provider/clinic profits from the margin
  // between their wholesale tier and the retail price the patient pays.
  // Manufacturer default is also W so catalog/browse views show market
  // pricing; once a provider is selected for an order the provider's tier
  // takes over via orderContextTier. Deselecting the provider returns to W.
  const activeTier = useMemo(() => {
    // Sales reps are coerced to `isPatient` for nav/checkout, but they buy at
    // their onboarded wholesale tier — not retail. This branch must run before
    // the isPatient check below or reps would silently fall through to "W".
    if (isSalesRep) return repTier;
    if (isPatient) return "W";
    if (isManufacturer) return orderContextTier || "W";
    return ownTier || "W";
  }, [isPatient, isSalesRep, repTier, isManufacturer, orderContextTier, ownTier]);

  // Resolve the price for a variant at the active tier
  // Manufacturer with a selected provider: per-provider custom override wins over tier price.
  // For patients (tier W): if W is missing, use the highest available price — never fall back to wholesale.
  const resolvePrice = useCallback(
    (variantId) => {
      // Custom pricing layer (manufacturer placing a master order for a specific provider)
      if (isManufacturer && selectedProviderId) {
        const custom = providerCustomPricing[selectedProviderId]?.[variantId];
        if (custom != null && Number.isFinite(custom)) return custom;
      }
      // Provider self-ordering: their own custom price map wins over tier.
      // Sales reps are excluded — they buy at repTier (handled below) and
      // never participate in per-provider custom pricing.
      if (isProvider && !isSalesRep) {
        const custom = ownCustomPricing[variantId];
        if (custom != null && Number.isFinite(custom)) return custom;
      }
      // Real patient (not a sales rep): the referring provider's retail price
      // wins. When unset, fall through — tierPricingData is {} for real
      // patients, so this returns null and getItemPrice uses variant.priceCents.
      if (isPatient && !isSalesRep) {
        const retail = providerRetailPricing[variantId];
        if (retail != null && Number.isFinite(retail)) return retail;
      }
      // Sales rep: manufacturer-set per-product override wins over tier price
      // (absolute cost for that variant) — but only a positive finite value
      // supersedes tier; zero/negative/non-finite fall through to tier below.
      if (isSalesRep) {
        const custom = repCustomPricing[variantId];
        if (custom != null && Number.isFinite(custom) && custom > 0) return custom;
      }
      const prices = tierPricingData[variantId];
      if (!prices) return null;
      if (prices[activeTier] != null) return prices[activeTier];
      if (isSalesRep) {
        // If the rep's onboarded tier isn't quoted for this variant, fall
        // back to retail (W) rather than silently dropping to a deeper
        // discount — the manufacturer hasn't priced this for reps yet.
        return prices.W ?? null;
      }
      if (isPatient) {
        // Patient safety: return the highest known price, not wholesale
        const allPrices = Object.values(prices).filter((p) => p != null && typeof p === "number");
        return allPrices.length > 0 ? Math.max(...allPrices) : null;
      }
      return prices.W ?? prices.D ?? null;
    },
    [
      activeTier,
      tierPricingData,
      isPatient,
      isSalesRep,
      isProvider,
      isManufacturer,
      selectedProviderId,
      providerCustomPricing,
      ownCustomPricing,
      providerRetailPricing,
      repCustomPricing,
    ]
  );

  // Get price at a specific tier (for comparison views)
  const getPriceAtTier = useCallback(
    (variantId, tier) => {
      const prices = tierPricingData[variantId];
      if (!prices) return null;
      return prices[tier] ?? null;
    },
    [tierPricingData]
  );

  // Get savings percentage compared to the most expensive tier (W retail,
  // falling back to D for back-compat with older data shapes that didn't
  // carry W). F was retired from the tier ladder — no longer a fallback.
  const getTierSavings = useCallback(
    (variantId, tier) => {
      const prices = tierPricingData[variantId];
      if (!prices || !prices[tier]) return 0;
      const baseline = prices.W ?? prices.D;
      if (!baseline || prices[tier] >= baseline) return 0;
      return Math.round(((baseline - prices[tier]) / baseline) * 100);
    },
    [tierPricingData]
  );

  const getCost = useCallback((variantId) => costData[variantId] ?? null, [costData]);

  const getMargin = useCallback(
    (variantId, tier) => {
      const cost = costData[variantId];
      if (cost == null) return null;
      const sell = tier ? getPriceAtTier(variantId, tier) : resolvePrice(variantId);
      if (!sell) return null;
      return Math.round(((sell - cost) / sell) * 100);
    },
    [costData, getPriceAtTier, resolvePrice]
  );

  // Tracks which variantIds have unsaved cost edits since the last flush.
  // setCost adds to this set; flushPendingCost reads + clears it on Save.
  // Editor's Cancel-revert path also reads it to know what to roll back.
  const dirtyCostKeysRef = useRef(new Set());

  // setCost updates local state only — backend persistence happens on
  // explicit Save Changes via flushPendingCost. This matches how tier
  // prices behave (editor-local until Save) so Cancel truly reverts.
  const setCost = useCallback((variantId, cents) => {
    setCostDataState((prev) => ({ ...prev, [variantId]: cents }));
    dirtyCostKeysRef.current.add(variantId);
  }, []);

  // Force-fire any unsaved cost edits to the backend. Called from the
  // product editor's handleSave. No-op if nothing's dirty.
  const flushPendingCost = useCallback(async () => {
    if (dirtyCostKeysRef.current.size === 0) return;
    const keys = Array.from(dirtyCostKeysRef.current);
    dirtyCostKeysRef.current.clear();
    await Promise.all(
      keys.map((variantId) =>
        saveCostData(variantId, costData[variantId]).catch((e) => console.error("flush cost failed", variantId, e))
      )
    );
  }, [costData]);

  // Drops dirty markers without saving. Used by the editor's Cancel path —
  // after the editor reverts costData state via setCost, those reverts get
  // re-marked as dirty; clearing the set tells flushPendingCost the next
  // Save can ignore them.
  const clearDirtyCostKeys = useCallback(() => {
    dirtyCostKeysRef.current.clear();
  }, []);

  // Re-key cost data when a variant's id changes (autoVariantId in the editor
  // derives the vid from form.id + dosage; editing dosage post-create rewrites
  // the vid). Without this, the cost stays stranded under the old key.
  const renameCostKey = useCallback((oldVid, newVid) => {
    if (!oldVid || !newVid || oldVid === newVid) return;
    setCostDataState((prev) => {
      if (prev[oldVid] == null) return prev;
      const next = { ...prev };
      next[newVid] = prev[oldVid];
      delete next[oldVid];
      return next;
    });
  }, []);

  // Assign a tier to an order provider. Persists to the manufacturer→provider
  // OrganizationProvider vertex (replaces the legacy peptideProviderTiers map
  // on the manufacturer's DoctorOffice). Local map is updated optimistically
  // so the manufacturer UI re-renders immediately, and rolled back on failure
  // so the displayed tier never silently diverges from what's persisted.
  const assignProviderTier = useCallback(
    (providerOrgId, tier) => {
      if (!TIER_KEYS.includes(tier)) return;
      let previousTier;
      setProviderTiers((prev) => {
        previousTier = prev[providerOrgId];
        return { ...prev, [providerOrgId]: tier };
      });
      return api.assignProviderPricingTier(providerOrgId, tier).catch((e) => {
        console.error("Failed to save provider tier:", e?.message || "unknown error");
        toast.error(t("hooks.usePeptidePricing.failedToSaveProviderTier"));
        setProviderTiers((prev) => ({ ...prev, [providerOrgId]: previousTier }));
        throw e;
      });
    },
    [t]
  );

  // Save per-provider custom price overrides. Lives on the same edge as
  // pricingTier. Optimistic local update + rollback on failure so the cart
  // never displays a saved-state that disagrees with persisted state.
  // Pass an empty customPrices map to clear all overrides for that provider.
  const saveProviderCustomPricing = useCallback(
    (providerOrgId, customPrices, recurringDefault) => {
      if (!providerOrgId) return Promise.resolve();
      const requestScopeKey = currentCatalogScopeKey;
      const requestGeneration = currentOwnedPricingScopeGeneration;
      if (isSalesRep && !requestScopeKey) return Promise.reject(new Error("pricing_scope_unavailable"));
      if (isSalesRep && (!catalogReadyForCurrentScope || !ownedPricingReadyForCurrentScope)) {
        return Promise.reject(new Error("pricing_baseline_not_ready"));
      }
      // Keep the lock across A → B → A generations. The obsolete A request
      // can still change A's server map, so returned actor A must wait for its
      // settlement and authoritative recovery fetch before submitting again.
      const saveLockKey = isSalesRep ? JSON.stringify([requestScopeKey, providerOrgId]) : "";
      if (saveLockKey && providerPricingSaveLocksRef.current.has(saveLockKey)) {
        return Promise.reject(new Error("pricing_save_in_progress"));
      }
      if (saveLockKey) providerPricingSaveLocksRef.current.add(saveLockKey);
      const appliedScope = appliedOwnedPricingScopeRef.current;
      const scopeChanged =
        isSalesRep && (appliedScope.scopeKey !== requestScopeKey || appliedScope.generation !== requestGeneration);
      if (isSalesRep) {
        const requestScope = {
          scopeKey: requestScopeKey,
          generation: requestGeneration,
        };
        appliedOwnedPricingScopeRef.current = requestScope;
        setOwnedPricingScope(requestScope);
      }
      let previousPricing;
      let previousRecurring;
      setProviderCustomPricing((prev) => {
        const scopedPrevious = scopeChanged ? {} : prev;
        previousPricing = scopedPrevious[providerOrgId];
        return { ...scopedPrevious, [providerOrgId]: { ...customPrices } };
      });
      if (recurringDefault) {
        setProviderRecurringDefaults((prev) => {
          previousRecurring = prev[providerOrgId];
          return { ...prev, [providerOrgId]: { ...recurringDefault } };
        });
      }
      const invalidateObsoleteSettlement = () => {
        const committedScope = currentOwnedPricingScopeRef.current;
        const sameActorReturned = committedScope.scopeKey === requestScopeKey && committedScope.generation !== requestGeneration;
        if (sameActorReturned) {
          appliedOwnedPricingScopeRef.current = {
            scopeKey: "",
            generation: 0,
          };
          setProviderCustomPricing({});
          setOwnedPricingScope({ scopeKey: "", generation: 0 });
          loadOwnedProviderCustomPricing({ refresh: true });
          return;
        }
        const currentAppliedScope = appliedOwnedPricingScopeRef.current;
        const obsoleteOptimisticMapIsStillApplied =
          currentAppliedScope.scopeKey === requestScopeKey && currentAppliedScope.generation === requestGeneration;
        if (obsoleteOptimisticMapIsStillApplied) {
          appliedOwnedPricingScopeRef.current = {
            scopeKey: "",
            generation: 0,
          };
          setProviderCustomPricing({});
          setOwnedPricingScope({ scopeKey: "", generation: 0 });
          setOwnedPricingLoad({ scopeKey: "", generation: 0, status: "idle" });
        }
      };
      let saveRequest;
      try {
        saveRequest = Promise.resolve(api.saveProviderCustomPricing(providerOrgId, customPrices, recurringDefault));
      } catch (e) {
        saveRequest = Promise.reject(e);
      }
      return saveRequest
        .then(
          (result) => {
            const committedScope = currentOwnedPricingScopeRef.current;
            const requestIsCurrent =
              requestScopeKey === committedScope.scopeKey && requestGeneration === committedScope.generation;
            if (isSalesRep && !requestIsCurrent) invalidateObsoleteSettlement();
            return result;
          },
          (e) => {
            const committedScope = currentOwnedPricingScopeRef.current;
            const requestIsCurrent =
              requestScopeKey === committedScope.scopeKey && requestGeneration === committedScope.generation;
            if (isSalesRep && !requestIsCurrent) {
              invalidateObsoleteSettlement();
              throw e;
            }
            console.error("Failed to save provider custom pricing:", e?.message || "unknown error");
            toast.error(t("hooks.usePeptidePricing.failedToSaveCustomPricing"));
            // If the slot didn't exist before, omit the key entirely on rollback —
            // assigning `undefined` would leave a junk key that pollutes Object.keys
            // iteration (e.g., the migration check in usePeptideCart).
            setProviderCustomPricing((prev) => {
              if (previousPricing === undefined) {
                const { [providerOrgId]: _omit, ...rest } = prev;
                return rest;
              }
              return { ...prev, [providerOrgId]: previousPricing };
            });
            if (recurringDefault) {
              setProviderRecurringDefaults((prev) => {
                if (previousRecurring === undefined) {
                  const { [providerOrgId]: _omit, ...rest } = prev;
                  return rest;
                }
                return { ...prev, [providerOrgId]: previousRecurring };
              });
            }
            throw e;
          }
        )
        .finally(() => {
          if (saveLockKey) providerPricingSaveLocksRef.current.delete(saveLockKey);
        });
    },
    [
      catalogReadyForCurrentScope,
      currentCatalogScopeKey,
      currentOwnedPricingScopeGeneration,
      isSalesRep,
      loadOwnedProviderCustomPricing,
      ownedPricingReadyForCurrentScope,
      t,
    ]
  );

  const saveProviderRetailPricing = useCallback(
    (retailPrices) => {
      const previous = providerRetailPricing;
      setProviderRetailPricing({ ...retailPrices });
      return api.saveProviderRetailPricing(retailPrices).catch((e) => {
        console.error("Failed to save provider retail pricing:", e?.message || "unknown error");
        toast.error(t("hooks.usePeptidePricing.failedToSaveRetailPricing", "Could not save patient prices."));
        setProviderRetailPricing(previous ?? {});
        throw e;
      });
    },
    [providerRetailPricing, t]
  );

  // Set the order context tier (when manufacturer selects which provider they're ordering for)
  const setOrderTierForProvider = useCallback(
    (providerOrgId) => {
      const tier = providerTiers[providerOrgId] || "W";
      setOrderContextTier(tier);
    },
    [providerTiers]
  );

  const clearOrderContext = useCallback(() => {
    setOrderContextTier(null);
    setSelectedProviderId("");
    setSelectedPatientId("");
    setOrderAssignType("patient");
  }, []);

  // Select an order provider (also applies tier pricing)
  const selectOrderProvider = useCallback(
    (providerId) => {
      setSelectedProviderId(providerId);
      if (providerId) {
        const provider = realProviders.find((p) => p.id === providerId);
        if (provider) {
          setOrderTierForProvider(provider.id);
        }
      } else {
        setOrderContextTier(null);
      }
    },
    [realProviders, setOrderTierForProvider]
  );

  // Select a patient
  const selectOrderPatient = useCallback((patientId) => {
    setSelectedPatientId(patientId);
  }, []);

  // Insert a freshly-created patient into the search list so the derived
  // `selectedPatient` (patients.find) resolves, then select it for the order.
  const addAndSelectPatient = useCallback((patient) => {
    const normalized = normalizePatientAccountRow(patient);
    if (!normalized) return;
    setPatients((prev) => [normalized, ...prev.filter((p) => p.id !== normalized.id)]);
    setSelectedPatientId(normalized.id);
  }, []);

  // Change assignment type and reset the other
  const changeAssignType = useCallback(
    (type) => {
      setOrderAssignType(type);
      if (type === "patient") {
        setSelectedProviderId("");
        setOrderContextTier(null);
      } else if (type === "instore") {
        // Walk-in sale — no provider, no patient. The manufacturer is the
        // counterparty so we also clear the order context tier (price
        // resolves back to retail W via the manufacturer activeTier rule).
        setSelectedProviderId("");
        setSelectedPatientId("");
        setOrderContextTier(null);
      } else {
        setSelectedPatientId("");
      }
    },
    [setOrderContextTier]
  );

  // Get list of order providers (manufacturer only) — tier keyed by org ID
  const orderProviders = useMemo(() => {
    if (!isManufacturer) return [];
    return realProviders.map((op) => ({
      ...op,
      tier: providerTiers[op.id] || "W",
    }));
  }, [isManufacturer, realProviders, providerTiers]);

  // Resolved selected provider / patient objects
  const selectedProvider = useMemo(() => {
    if (!selectedProviderId) return null;
    return orderProviders.find((p) => p.id === selectedProviderId) || null;
  }, [selectedProviderId, orderProviders]);

  const selectedPatient = useMemo(() => {
    if (!selectedPatientId) return null;
    return patients.find((p) => p.id === selectedPatientId) || null;
  }, [selectedPatientId, patients]);

  return {
    isManufacturer,
    isProvider,
    isPatient,
    isSalesRep,
    repTier,
    actualIsManufacturer,
    actualIsProvider,
    actualIsPatient,
    activeTier,
    ownTier,
    resolvePrice,
    getPriceAtTier,
    getTierSavings,
    providerTiers,
    assignProviderTier,
    providerCustomPricing,
    providerRecurringDefaults,
    ownCustomPricing,
    repCustomPricing,
    providerRetailPricing,
    activeSurchargeEnabled,
    providerSurchargeDisabled,
    isActiveSurchargeEnabledFor: (providerOrgId) => isProviderSurchargeEnabled(providerSurchargeDisabled, providerOrgId),
    saveProviderCustomPricing,
    saveProviderRetailPricing,
    // Refetch ownership-scoped custom pricing on demand (drawer mount, retry
    // after a failed initial load, etc.). Useful when another rep or a
    // supervisor has updated overrides outside this client's session.
    loadOwnedProviderCustomPricing,
    catalog,
    catalogReadyForCurrentScope,
    catalogLoadStatus,
    ownedPricingReadyForCurrentScope,
    ownedPricingLoadStatus,
    shippingMethods,
    pricingLoaded,
    orderProviders,
    orderContextTier,
    setOrderContextTier,
    setOrderTierForProvider,
    clearOrderContext,
    tierPricingData,
    // Order assignment
    orderAssignType,
    changeAssignType,
    selectedProviderId,
    selectOrderProvider,
    selectedPatientId,
    selectOrderPatient,
    addAndSelectPatient,
    selectedProvider,
    selectedPatient,
    patients,
    searchPatients,
    providersLoading,
    patientsLoading,
    referralData,
    costData: isManufacturer ? costData : {},
    getCost: isManufacturer ? getCost : () => null,
    getMargin: isManufacturer ? getMargin : () => null,
    setCost,
    flushPendingCost,
    renameCostKey,
    clearDirtyCostKeys,
    refreshPricing,
  };
}

export function ProvidePeptidePricing({ children }) {
  const values = usePeptidePricingProvider();
  return <peptidePricingContext.Provider value={values}>{children}</peptidePricingContext.Provider>;
}

export const usePeptidePricing = () => {
  const ctx = useContext(peptidePricingContext);
  if (!ctx) {
    return {
      isManufacturer: false,
      isProvider: false,
      isPatient: false,
      isSalesRep: false,
      repTier: null,
      actualIsManufacturer: false,
      actualIsProvider: false,
      actualIsPatient: false,
      activeTier: "W",
      ownTier: "W",
      resolvePrice: () => null,
      getPriceAtTier: () => null,
      getTierSavings: () => 0,
      providerTiers: {},
      assignProviderTier: () => {},
      providerCustomPricing: {},
      providerRecurringDefaults: {},
      ownCustomPricing: {},
      repCustomPricing: {},
      activeSurchargeEnabled: true,
      providerSurchargeDisabled: {},
      isActiveSurchargeEnabledFor: () => true,
      saveProviderCustomPricing: async () => {},
      loadOwnedProviderCustomPricing: async () => ({ ok: true }),
      catalog: [],
      catalogReadyForCurrentScope: false,
      catalogLoadStatus: "idle",
      ownedPricingReadyForCurrentScope: false,
      ownedPricingLoadStatus: "idle",
      shippingMethods: DEFAULT_PEPTIDE_SHIPPING_METHODS,
      pricingLoaded: false,
      orderProviders: [],
      orderContextTier: null,
      setOrderContextTier: () => {},
      setOrderTierForProvider: () => {},
      clearOrderContext: () => {},
      tierPricingData: {},
      orderAssignType: "patient",
      changeAssignType: () => {},
      selectedProviderId: "",
      selectOrderProvider: () => {},
      selectedPatientId: "",
      selectOrderPatient: () => {},
      addAndSelectPatient: () => {},
      selectedProvider: null,
      selectedPatient: null,
      patients: [],
      searchPatients: () => {},
      providersLoading: false,
      patientsLoading: false,
      referralData: null,
      costData: {},
      getCost: () => null,
      getMargin: () => null,
      setCost: () => {},
      flushPendingCost: async () => {},
      renameCostKey: () => {},
      clearDirtyCostKeys: () => {},
      refreshPricing: async () => {},
    };
  }
  return ctx;
};
